import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {createStyles, Divider, Grid} from '@mantine/core';
import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';

import {AntibodyComponentType} from 'antibodies/AntibodyConstants';
import {useAntibodyFormatsQuery} from 'antibodies/hooks/useAntibodyFormatsQuery';
import {useImplementedAntibodySchemaInterfacesQuery} from 'antibodies/hooks/useImplementedAntibodySchemaInterfacesQuery';
import AntibodyComponentSvg from 'antibodies/nested-data-creation/antibody-component-visualization/AntibodyComponentSvg';
import {buildAntibodySchemaFormatMaps} from 'antibodies/nested-data-creation/AntibodyNestedDataCreationUtil';
import {
  ALLOWED_ANTIBODY_FORMAT_IDS,
  NestedDataType,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {GetNDCSchemaConfig} from 'antibodies/nested-data-creation/panes/__generated/GetNDCSchemaConfig';
import {RegistryIds} from 'antibodies/nested-data-creation/panes/__generated/RegistryIds';
import AntibodyFormatPicker, {
  type AntibodyTileProps,
} from 'antibodies/schema-configuration/AntibodyFormatPicker';
import {assert} from 'Asserts';
import {OBJECTS_REGISTERABLE_PERMISSION} from 'auth/PermissionConstants';
import {pluralize} from 'BenchlingUtil';
import {type TagSchema} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import RegistrySelector from 'registry/RegistrySelector';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {AntibodyFormatGraphQL} from '../../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

const useStyles = createStyles((theme) => ({
  formatPicker: {
    h4: {
      marginBottom: theme.spacing.sm,
    },
    p: {
      marginBottom: theme.spacing.md,
    },
  },
  buttonWrapper: {
    width: 'max-content',
  },
  button: {
    border: 'none',
    borderRadius: '4px',
    padding: 0,
  },
  selectedButton: {
    border: `1px solid ${Color.BLUE_450}`,
  },
  buttonLabel: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    display: 'flex',
    justifyContent: 'center',
    marginTop: '4px',
  },
  inputOutputSection: {
    display: 'flex',
    gap: '16px',
  },
  banner: {
    marginTop: '16px',
  },
}));

interface InputOutputComponentButtonProps {
  componentType: AntibodyComponentType;
  componentName: string;
  isSelected: boolean;
}

function InputOutputComponentButton({
  componentType,
  componentName,
  isSelected,
}: InputOutputComponentButtonProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.buttonWrapper}>
      <button
        className={classNames(classes.button, {
          [classes.selectedButton]: isSelected,
        })}
        onClick={noop}
        type="button"
        aria-label={`${componentName} option`}
        aria-pressed={isSelected}
      >
        <AntibodyComponentSvg componentType={componentType} size={120} />
      </button>
      <div className={classes.buttonLabel}>{componentName}</div>
    </div>
  );
}

export const REGISTRY_IDS_QUERY = gql`
  query RegistryIds @skipAudit @engComponent(name: REGISTRY) {
    registries {
      ... on RegistryNode {
        id
      }
    }
  }
`;

export const NDC_CONFIG_QUERY = gql`
  query GetNDCSchemaConfig($registryId: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    ndcSchemaConfig(registryId: $registryId) {
      ... on NDCSchemaConfigNode {
        aaDomainSchemaId
        dnaPairSchemaId
      }
    }
  }
`;

/**
 * Nested data creation modal step that allows the user to select the format of the antibodies they want to create.
 *
 * Current limitations:
 * - Only supports selecting from antibody formats that involve a single variable pair (VH/VL).
 * - The input and output components are not configurable by the user: the input is always DNA domain
 *   and both AA domains and AA chains must always be created as outputs.
 */
export interface SelectFormatPaneProps {
  selectedRegistryId: string | null;
  setSelectedRegistryIdAndDependentConfig: (
    registryId: string | null,
    pairSchemaId: string | null,
    constantDomainSchemaId: string | null
  ) => void;
  selectedFormatId: string | null;
  setSelectedFormat: (format: AntibodyFormatGraphQL | null) => void;
  selectedSchemaId: string | null;
  setSelectedSchemaId: (schemaId: string | null) => void;
  startingDataType: NestedDataType;
  outputTypes: ReadonlySet<NestedDataType>;
}

export default function SelectFormatPane({
  selectedRegistryId,
  setSelectedRegistryIdAndDependentConfig,
  selectedFormatId,
  setSelectedFormat,
  selectedSchemaId,
  setSelectedSchemaId,
  startingDataType,
  outputTypes,
}: SelectFormatPaneProps): JSX.Element {
  const {classes} = useStyles();
  const antibodyFormats = useAntibodyFormatsQuery();

  const {
    status: implementedAntibodySchemaInterfacesQueryStatus,
    schemaInterfaces: implementedAntibodySchemaInterfaces,
  } = useImplementedAntibodySchemaInterfacesQuery();
  const {schemaIdToFormat, formatIdToSchemaIds, allSchemaIds} = useMemo(
    () => buildAntibodySchemaFormatMaps(implementedAntibodySchemaInterfaces),
    [implementedAntibodySchemaInterfaces]
  );

  // After a registry is selected, we need to wait for the dependent config to be fetched before we can
  // set the selected registry and dependent config, so store it temporarily in local state.
  const [unprocessedSelectedRegistryId, setUnprocessedSelectedRegistryId] = useState<string | null>(
    selectedRegistryId
  );
  const [registryErrorMessage, setRegistryErrorMessage] = useState<string | null>(null);
  const registryIdQueryResult = useBenchlingQuery<RegistryIds>(REGISTRY_IDS_QUERY);
  const ndcConfigQueryResult = useSkippableBenchlingQuery<GetNDCSchemaConfig>(NDC_CONFIG_QUERY, {
    variables: {registryId: unprocessedSelectedRegistryId!},
    skip: unprocessedSelectedRegistryId == null,
  });

  // Extract the results for easier useEffect dependencies. We can't use the query results as a dependency
  // because useEffect does a shallow comparison, and the identity of the object changes continuously.
  const ndcConfigIsInaccessible =
    ndcConfigQueryResult.type === QueryResultType.SUCCESS &&
    isInaccessible(ndcConfigQueryResult.data.ndcSchemaConfig);
  const dnaPairSchemaId =
    ndcConfigQueryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(ndcConfigQueryResult.data.ndcSchemaConfig)
      ? ndcConfigQueryResult.data.ndcSchemaConfig.dnaPairSchemaId
      : null;
  const aaDomainSchemaId =
    ndcConfigQueryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(ndcConfigQueryResult.data.ndcSchemaConfig)
      ? ndcConfigQueryResult.data.ndcSchemaConfig.aaDomainSchemaId
      : null;

  useEffect(() => {
    // If there is only one registry, select it by default. RegistrySelector would do this itself, but we
    // don't render RegistrySelector if there is only one registry, so we need to do it here.
    if (
      registryIdQueryResult.type === QueryResultType.SUCCESS &&
      registryIdQueryResult.data.registries.length === 1 &&
      selectedRegistryId !== registryIdQueryResult.data.registries[0].id
    ) {
      setUnprocessedSelectedRegistryId(registryIdQueryResult.data.registries[0].id);
    }
  }, [registryIdQueryResult, selectedRegistryId]);

  useEffect(() => {
    if (ndcConfigQueryResult.type === QueryResultType.SUCCESS) {
      // If the registry was selected, it should be accessible.
      assert(!ndcConfigIsInaccessible, 'Expected registry to be accessible');
      // If the query is a success and the registry is accessible, missing schema ids mean they don't exist.
      const missingSchemas: Array<string> = [];
      if (dnaPairSchemaId == null) {
        missingSchemas.push('DNA pair');
      }
      if (aaDomainSchemaId == null) {
        missingSchemas.push('AA domain');
      }
      if (missingSchemas.length > 0) {
        setRegistryErrorMessage(
          `This registry is missing the ${missingSchemas.join(' and ')} ${pluralize(missingSchemas.length, 'schema', 'schemas')} necessary to create antibodies.`
        );
      } else {
        setRegistryErrorMessage(null);
      }
      setSelectedRegistryIdAndDependentConfig(
        unprocessedSelectedRegistryId,
        dnaPairSchemaId || null,
        aaDomainSchemaId || null
      );
    }
  }, [
    ndcConfigQueryResult.type,
    ndcConfigIsInaccessible,
    dnaPairSchemaId,
    aaDomainSchemaId,
    setSelectedRegistryIdAndDependentConfig,
    unprocessedSelectedRegistryId,
  ]);

  const antibodyTileProps: Array<AntibodyTileProps> = antibodyFormats
    .filter(({id}) => ALLOWED_ANTIBODY_FORMAT_IDS.includes(id))
    // Only show formats that have a defined antibody schema using this format
    .filter(({id}) => formatIdToSchemaIds.has(id))
    .map(
      (format): AntibodyTileProps => ({
        format,
        showLabels: false,
        disabled: false,
      })
    );

  function onSelectFormatId(formatId: string): void {
    const format = antibodyFormats.find(({id}) => id === formatId);
    assert(format != null, 'Expected format to be found in antibodyFormats');
    setSelectedFormat(format);
    if (selectedSchemaId != null) {
      assert(schemaIdToFormat.has(selectedSchemaId), 'Expected schema to be found in schemaIdToFormat');
      if (formatId !== schemaIdToFormat.get(selectedSchemaId)!.id) {
        setSelectedSchemaId(null);
      }
    }
  }

  const onSelectSchema = (tagSchema: TagSchema | null): void => {
    setSelectedSchemaId(tagSchema?.id ?? null);
    if (tagSchema != null) {
      assert(schemaIdToFormat.has(tagSchema.id), 'Expected schema to be found in schemaIdToFormat');
      setSelectedFormat(schemaIdToFormat.get(tagSchema.id)!);
    }
  };

  const filteredSchemaIds =
    selectedFormatId == null ? allSchemaIds : formatIdToSchemaIds.get(selectedFormatId)!;

  const isLoading =
    registryIdQueryResult.type === QueryResultType.PENDING ||
    ndcConfigQueryResult.type === QueryResultType.PENDING;

  // Show the selector if there is more than one registry or if there is an error.
  const showRegistrySelector =
    (registryIdQueryResult.type === QueryResultType.SUCCESS &&
      registryIdQueryResult.data.registries.length !== 1) ||
    registryErrorMessage != null;

  return (
    <div>
      {implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.ERROR && (
        <div className={classes.banner}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading antibody schemas" />
        </div>
      )}

      <div className={classes.formatPicker}>
        <h4>Format</h4>
        <p>Select the format of the antibodies you want to create</p>
        <AntibodyFormatPicker
          antibodyTileProps={antibodyTileProps}
          disabled={false}
          selectedFormat={selectedFormatId}
          setSelectedFormat={onSelectFormatId}
          enableCreateNew={false}
        />
      </div>

      <Grid mt="md">
        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'schemaSelector'})}>
          <FormGroup hasError={implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.ERROR}>
            <FieldLabel label="Schema" isRequired={true} />
            {implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.PENDING ? (
              <div>
                <HorizontalThrobber />
              </div>
            ) : (
              <>
                <TagSchemaSelector
                  selectedTagSchemaId={selectedSchemaId}
                  onChange={onSelectSchema}
                  tagSchemaFilterFn={(tagSchema) => filteredSchemaIds.has(tagSchema.id)}
                  shouldDefaultIfSingleItem={true}
                  requiredPermissions={[OBJECTS_REGISTERABLE_PERMISSION]}
                />
              </>
            )}
          </FormGroup>
        </Grid.Col>

        {showRegistrySelector && (
          <Grid.Col span={6}>
            <FormGroup hasError={registryErrorMessage != null}>
              <FieldLabel label="Registry" isRequired={true} />
              {isLoading ? (
                <HorizontalThrobber />
              ) : (
                <>
                  <RegistrySelector
                    onChange={({target: {value: registry}}) => {
                      setUnprocessedSelectedRegistryId(registry?.id ?? null);
                    }}
                    registryId={unprocessedSelectedRegistryId}
                  />
                  {registryErrorMessage != null && (
                    <div className={classes.banner}>
                      <InlineBanner bannerType={InlineBannerType.ERROR} text={registryErrorMessage} />
                    </div>
                  )}
                </>
              )}
            </FormGroup>
          </Grid.Col>
        )}
      </Grid>

      <Divider my="md" />

      <h4>Nested components</h4>
      <p>
        Select the input component and any additional output components you want to generate alongside the
        antibodies
      </p>

      <Grid>
        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'inputsSection'})}>
          <h5>INPUT COMPONENT</h5>
          <div className={classes.inputOutputSection}>
            <InputOutputComponentButton
              componentType={AntibodyComponentType.DNA_DOMAIN}
              componentName="DNA domain"
              isSelected={startingDataType === NestedDataType.DNA_DOMAIN}
            />
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_DOMAIN}
              componentName="AA domain"
              isSelected={startingDataType === NestedDataType.AA_DOMAIN}
            />
          </div>
        </Grid.Col>

        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'outputsSection'})}>
          <h5>OUTPUT COMPONENTS</h5>
          <div className={classes.inputOutputSection}>
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_DOMAIN}
              componentName="AA domain"
              isSelected={outputTypes.has(NestedDataType.AA_DOMAIN)}
            />
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_CHAIN}
              componentName="AA chain"
              isSelected={outputTypes.has(NestedDataType.AA_CHAIN)}
            />
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
}
